/* ============================================================
   SHAHAN STORE - Premium JavaScript
   ============================================================ */

(function() {
  'use strict';

  /* --- Scroll Reveal Observer --- */
  const initScrollReveal = () => {
    const elements = document.querySelectorAll('.shahan-reveal');
    if (!elements.length) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      elements.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    });

    elements.forEach(el => observer.observe(el));
  };

  /* --- Carousel --- */
  const initCarousels = () => {
    document.querySelectorAll('.shahan-carousel').forEach(carousel => {
      const track = carousel.querySelector('.shahan-carousel__track');
      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');
      if (!track) return;

      const getScrollAmount = () => {
        const item = track.querySelector('.shahan-carousel__item');
        return item ? item.offsetWidth + 24 : 300;
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
        });
      }

      // Touch/swipe support via native scroll
      let isDown = false, startX, scrollLeft;
      track.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
        track.style.cursor = 'grabbing';
      });
      track.addEventListener('mouseleave', () => { isDown = false; track.style.cursor = ''; });
      track.addEventListener('mouseup', () => { isDown = false; track.style.cursor = ''; });
      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        track.scrollLeft = scrollLeft - (x - startX) * 1.5;
      });
    });
  };

  /* --- FAQ Accordion --- */
  const initFAQ = () => {
    document.querySelectorAll('.shahan-faq__q').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.shahan-faq__item');
        const isActive = item.classList.contains('is-active');

        // Close all others in same section
        const container = item.closest('.shahan-faq__wrap') || item.closest('[class*="shahan-faq"]');
        container.querySelectorAll('.shahan-faq__item.is-active').forEach(open => {
          if (open !== item) open.classList.remove('is-active');
        });

        item.classList.toggle('is-active', !isActive);

        // Accessibility
        btn.setAttribute('aria-expanded', String(!isActive));
      });
    });
  };

  /* --- Newsletter Popup --- */
  const initNewsletterPopup = () => {
    const popup = document.querySelector('.shahan-popup');
    const overlay = document.querySelector('.shahan-popup-overlay');
    if (!popup || !overlay) return;

    const closeBtn = popup.querySelector('.shahan-popup__close');
    const key = 'shahan_newsletter_dismissed';

    // Don't show if already dismissed
    if (localStorage.getItem(key)) return;

    const show = () => {
      popup.classList.add('is-visible');
      overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      popup.querySelector('input[type="email"]')?.focus();
    };

    const hide = () => {
      popup.classList.remove('is-visible');
      overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      localStorage.setItem(key, 'true');
    };

    // Show after 5 seconds or 60% scroll
    let scrollShown = false;
    const scrollHandler = () => {
      if (scrollShown) return;
      const scrolled = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (scrolled > 0.6) {
        scrollShown = true;
        show();
        window.removeEventListener('scroll', scrollHandler);
      }
    };

    setTimeout(() => {
      if (!localStorage.getItem(key)) show();
    }, 5000);

    window.addEventListener('scroll', scrollHandler, { passive: true });

    closeBtn?.addEventListener('click', hide);
    overlay.addEventListener('click', hide);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('is-visible')) {
        hide();
      }
    });
  };

  /* --- Wishlist --- */
  const initWishlist = () => {
    const WISHLIST_KEY = 'shahan_wishlist';

    const getWishlist = () => {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch {
        return [];
      }
    };

    const saveWishlist = (list) => {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      updateBadge();
      updateButtons();
      document.dispatchEvent(new CustomEvent('shahan:wishlist-updated'));
    };

    const updateBadge = () => {
      const badges = document.querySelectorAll('.shahan-wishlist-badge__count');
      const count = getWishlist().length;
      badges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });
    };

    const updateButtons = () => {
      const wishlist = getWishlist();
      document.querySelectorAll('.shahan-wishlist-btn').forEach(btn => {
        const id = btn.dataset.productId;
        btn.classList.toggle('is-active', wishlist.includes(id));
          btn.setAttribute('aria-label',
          wishlist.includes(id) ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'
        );
      });
    };

    const showToast = (msg) => {
      let el = document.querySelector('.shahan-toast');
      if (!el) {
        el = document.createElement('div');
        el.className = 'shahan-toast';
        document.body.appendChild(el);
      }
      el.textContent = msg;
      el.classList.add('is-visible');
      clearTimeout(el._t);
      el._t = setTimeout(() => el.classList.remove('is-visible'), 2200);
    };

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.shahan-wishlist-btn');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const productId = btn.dataset.productId;
      if (!productId) return;

      let wishlist = getWishlist();
      if (wishlist.includes(productId)) {
        wishlist = wishlist.filter(id => id !== productId);
        showToast('تمت الإزالة من المفضلة');
      } else {
        wishlist.push(productId);
        showToast('تمت الإضافة إلى المفضلة');
      }
      saveWishlist(wishlist);
    });

    document.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-shahan-wl-remove]');
      if (!rm) return;
      e.preventDefault();
      const id = rm.getAttribute('data-shahan-wl-remove');
      let wl = getWishlist();
      wl = wl.filter(x => x !== id);
      saveWishlist(wl);
    });

    const renderWishlistPage = () => {
      const grid = document.querySelector('[data-shahan-wishlist-page]');
      if (!grid) return;
      const ids = getWishlist();
      let count = 0;
      grid.querySelectorAll('.shahan-wl-card').forEach((card) => {
        const id = card.getAttribute('data-shahan-wl-id');
        if (ids.indexOf(id) !== -1) { card.hidden = false; count++; }
        else { card.hidden = true; }
      });
      const empty = grid.parentNode.querySelector('[data-shahan-wishlist-empty]');
      if (empty) empty.hidden = count > 0;
    };

    renderWishlistPage();
    document.addEventListener('shahan:wishlist-updated', renderWishlistPage);

    updateBadge();
    updateButtons();
  };

  /* --- Smooth Scroll for Anchor Links --- */
  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (!href || href === '#') return;
        let target;
        try { target = document.querySelector(href); } catch (_) { return; }
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  };

  /* --- Parallax Effect for Hero --- */
  const initParallax = () => {
    const hero = document.querySelector('.shahan-hero');
    if (!hero) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const bg = hero.querySelector('.shahan-hero__bg img');
    if (!bg) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.scrollY;
          if (scrolled < hero.offsetHeight) {
            bg.style.transform = `translateY(${scrolled * 0.3}px) scale(1.05)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  };

  /* --- Countdown Timer (for offers) --- */
  const initCountdown = () => {
    document.querySelectorAll('[data-countdown]').forEach(el => {
      const endDate = new Date(el.dataset.countdown).getTime();
      if (isNaN(endDate)) return;

      const update = () => {
        const now = Date.now();
        const diff = Math.max(0, endDate - now);

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        const daysEl = el.querySelector('[data-days]');
        const hoursEl = el.querySelector('[data-hours]');
        const minutesEl = el.querySelector('[data-minutes]');
        const secondsEl = el.querySelector('[data-seconds]');

        if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
        if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
        if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
        if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
      };

      update();
      setInterval(update, 1000);
    });
  };

  /* --- Back to Top Button --- */
  const initBackToTop = () => {
    const btn = document.querySelector('.shahan-back-to-top');
    if (!btn) return;

    window.addEventListener('scroll', () => {
      btn.classList.toggle('is-visible', window.scrollY > 600);
    }, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  /* --- Initialize Everything --- */
  const init = () => {
    initScrollReveal();
    initCarousels();
    initFAQ();
    initNewsletterPopup();
    initWishlist();
    initSmoothScroll();
    initParallax();
    initCountdown();
    initBackToTop();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
